/**
 * Entity detail — the three-pane view (doc 147 §5.1, §9 Phase 1):
 * schema (fields) · relationships (in/out references) · usage (paths, surfaces).
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import type { DomainLayer } from '@atlas-manifest/types';
import { Icon } from '../components/Icon.js';
import { EmptyState } from '../components/EmptyState.js';
import { Chip } from '../components/Chip.js';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { inferEntityShape, ENTITY_SHAPE_ICONS } from '../icons/catalog.js';
import { resolveEntityKind } from '../state/entityClassification.js';
import {
  entityById,
  domainById,
  outgoingRefs,
  incomingRefs,
  pathForEntity,
  surfacesForEntity,
  entityDegree,
  surfacesUsingEntity,
  cloudFunctionsForEntity,
  eventsForEntity,
} from '../state/selectors.js';
import { navigate, navigateToEntity, navigateToObject, navigateToObjectKind, routeToHash } from '../state/hashRouter.js';
import { PresenceContainer } from './PresenceContainer.js';
import { AnnotationsContainer } from './AnnotationsContainer.js';
import { EntityHistoryContainer } from './EntityHistoryContainer.js';
import { AskAtlasToEditBox } from '../components/AskAtlasToEditBox.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';
import { EntityInsightTiles } from './EntityInsightTiles.js';
import { MiniEgoGraph } from './MiniEgoGraph.js';
import { SelectedFieldDetail, formatTypescriptField } from './SelectedFieldDetail.js';
import { WorkflowTrail } from './WorkflowTrail.js';
import { createInsightIndex, type InsightIndex } from '../state/entityInsights.js';
import { useAppStore } from '../state/store.js';
import { mapKey, type KeyContext } from '../state/keyboardMap.js';
import { useEffect, useMemo } from 'react';

/** Provenance-tier tone mapping — reuses existing semantic tokens, no new CSS. */
const SOURCE_TONE: Record<import('@atlas-manifest/types').ReferenceSource, string | undefined> = {
  annotation: 'good',
  prose: undefined,
  'ai-gapfill': 'caution',
  'v1-fallback': 'danger',
  design: 'info',
};

/**
 * Kind-aware empty-state messages for the relationships section.
 * Some entity kinds are structurally designed to never be referenced.
 */
function getRelationshipsEmptyStateMessage(entityKind: import('@atlas-manifest/types').EntityKind | undefined): string {
  if (entityKind === 'props') {
    return 'Leaf by design — kind: "props" entities are structurally never referenced';
  }
  return 'This entity stands alone in the model.';
}

export interface EntityDetailViewProps {
  manifest: AtlasManifest;
  entityId: string;
  pinned: string[];
  onTogglePin: (entityId: string) => void;
  onEntitySelected?: (entityId: string) => void;
  selectedField?: string;
}

export function EntityDetailView({
  manifest,
  entityId,
  pinned,
  onTogglePin,
  onEntitySelected,
  selectedField,
}: EntityDetailViewProps): React.JSX.Element {
  const entity = entityById(manifest, entityId);
  const index: InsightIndex = useMemo(() => createInsightIndex(manifest), [manifest]);
  const { entityTrail } = useAppStore();

  // Update the active entity id when this entity is selected or changes
  useEffect(() => {
    onEntitySelected?.(entityId);
  }, [entityId, onEntitySelected]);

  // Entity-page-scoped keyboard dispatcher: open-in-new-tab, copy-typescript,
  // copy-firestore-path. Mirrors WorkspaceView's own scoped listener pattern
  // — these actions only make sense while an entity page is mounted, so they
  // live here rather than in App.tsx's global listener.
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      const target = event.target as Element | null;
      const tag = (target as HTMLElement | null)?.tagName;
      const inputActive =
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        target?.getAttribute('contenteditable') === 'true';
      const ctx: KeyContext = {
        workspaceMode: { kind: 'idle' },
        inputActive,
        entityPage: { entityId, ...(selectedField ? { selectedField } : {}) },
      };
      const action = mapKey(event, ctx);
      if (!action) return;
      switch (action.kind) {
        case 'open-in-new-tab':
          event.preventDefault();
          window.open(routeToHash({ kind: 'entity', id: entityId }), '_blank', 'noopener');
          return;
        case 'copy-typescript': {
          if (!selectedField) return;
          const field = entity?.fields.find((f) => f.name === selectedField);
          if (field) {
            event.preventDefault();
            copyToClipboard(formatTypescriptField(field));
          }
          return;
        }
        case 'copy-firestore-path':
          if (entity?.firestorePath) {
            event.preventDefault();
            copyToClipboard(entity.firestorePath);
          }
          return;
        default:
          return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [entityId, selectedField, entity]);

  if (!entity) {
    return (
      <EmptyState icon="help" title={`No entity “${entityId}”`} detail="It may have been renamed or removed.">
        <button className="btn btn-ghost" onClick={() => navigate({ kind: 'welcome' })}>
          Back to welcome
        </button>
      </EmptyState>
    );
  }

  const domain = domainById(manifest, entity.domain);
  const out = outgoingRefs(manifest, entity.id);
  const incoming = incomingRefs(manifest, entity.id);
  const path = pathForEntity(manifest, entity.id);
  const surfaces = surfacesForEntity(manifest, entity.id);
  const shape = inferEntityShape(entity);
  const entityKind = resolveEntityKind(entity);
  const lifecycle = entity.lifecycle ?? 'committed';
  const isPinned = pinned.includes(entity.id);
  const degree = entityDegree(manifest, entity.id);
  const heroSurfaces = surfacesUsingEntity(manifest, entity.id);
  const selectedFieldIsReal = Boolean(
    selectedField && entity.fields.some((f) => f.name === selectedField),
  );

  return (
    <article>
      <ObjectHeader
        object={entity}
        icon={ENTITY_SHAPE_ICONS[shape]}
        iconLabel={`${shape} entity`}
        badges={
          domain
            ? [
                { label: entity.domain, type: 'domain' as const },
                { label: (domain.layer ?? 'L5') as DomainLayer, type: 'layer' as const },
              ]
            : []
        }
        title={entity.name}
        docRef={
          <>
            {entity.firestorePath ?? 'ephemeral · not persisted'} · <span>{entity.docRef}</span>
          </>
        }
        tags={entity.tags}
        chips={[
          { label: entityKind, title: 'Structural kind (docs/data-model-completion-strategy.html §4)' },
          { label: lifecycle, tone: lifecycle === 'proposed' ? 'caution' : undefined, title: 'Design-authority lifecycle stage' },
        ]}
        actions={
          <>
            <button
              className="btn btn-ghost"
              onClick={() => navigate({ kind: 'sandbox', seedEntity: entity.id })}
            >
              <Icon name="edit" size="sm" />
              Edit in Sandbox
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => navigate({ kind: 'simulate', focus: entity.id })}
            >
              <Icon name="science" size="sm" />
              Simulate
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => navigate({ kind: 'ask', focus: entity.id })}
            >
              <Icon name="auto_awesome" size="sm" />
              Ask Atlas
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => navigate({ kind: 'impact', id: entity.id })}
            >
              <Icon name="change_history" size="sm" />
              Impact
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => navigate({ kind: 'trace', from: entity.id })}
            >
              <Icon name="route" size="sm" />
              Trace
            </button>
            <button
              className="tree-row"
              style={{ width: 'auto' }}
              aria-pressed={isPinned}
              aria-label={isPinned ? 'Unpin entity' : 'Pin entity'}
              onClick={() => onTogglePin(entity.id)}
            >
              <Icon name="push_pin" size="md" color={isPinned ? 'var(--brand-subtle)' : undefined} />
            </button>
            <button
              className="btn btn-ghost"
              aria-label="Copy link"
              onClick={() => {
                navigator.clipboard
                  ?.writeText(window.location.href)
                  .catch((err: unknown) => console.debug('[atlas] clipboard denied:', err));
              }}
            >
              <Icon name="link" size="sm" />
              Copy link
            </button>
          </>
        }
      />
      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}
      <dl className="sheet-hero-stats" aria-label="stats">
        <div>
          <dt>FIELDS</dt>
          <dd>{entity.fields.length}</dd>
        </div>
        <div>
          <dt>REFS OUT</dt>
          <dd>{degree.outDegree}</dd>
        </div>
        <div className={degree.inDegree > 5 ? 'warn' : ''}>
          <dt>REFS IN</dt>
          <dd>{degree.inDegree}</dd>
        </div>
        <div className="info">
          <dt>SURFACES</dt>
          <dd>{heroSurfaces.length}</dd>
        </div>
      </dl>
      <div aria-label="Entity ego graph">
        <MiniEgoGraph key={entity.id} manifest={manifest} entityId={entity.id} onNavigateEntity={navigateToEntity} />
      </div>
      <PresenceContainer entityId={entity.id} />
      <AskAtlasToEditBox
        kind="entity"
        selectorId={entity.id}
        currentObject={entity as unknown as Record<string, unknown>}
        manifest={manifest}
        onAsk={(opts) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds)}
      />
      {entity.description && <p className="entity-desc">{entity.description}</p>}

      {/* Enrichment metadata block */}
      {(entity.rationale != null ||
        entity.writeOwner != null ||
        (entity.invariants?.length ?? 0) > 0 ||
        (entity.readPatterns?.length ?? 0) > 0 ||
        (entity.aiEnrichedFields?.length ?? 0) > 0) ? (
        <div className="enrichment-block" data-testid="enrichment-block">
          {entity.aiEnrichedFields && entity.aiEnrichedFields.length > 0 && (
            <div className="enrichment-field enrichment-field--ai">
              <span className="enrichment-label">AI enriched</span>
              <span className="enrichment-ai-badge" title={`Fields filled by AI: ${entity.aiEnrichedFields.join(', ')}`}>
                ✦ {entity.aiEnrichedFields.join(' · ')}
              </span>
            </div>
          )}
          {entity.rationale && (
            <div className="enrichment-field">
              <span className="enrichment-label">Rationale</span>
              <span className="enrichment-value">{entity.rationale}</span>
            </div>
          )}
          {entity.writeOwner && (
            <div className="enrichment-field">
              <span className="enrichment-label">Write owner</span>
              <span className="enrichment-badge">{entity.writeOwner}</span>
            </div>
          )}
          {entity.invariants && entity.invariants.length > 0 && (
            <div className="enrichment-field">
              <span className="enrichment-label">Invariants</span>
              <ul className="enrichment-list">
                {entity.invariants.map((inv, i) => (
                  <li key={i}>{inv}</li>
                ))}
              </ul>
            </div>
          )}
          {entity.readPatterns && entity.readPatterns.length > 0 && (
            <div className="enrichment-field">
              <span className="enrichment-label">Read patterns</span>
              <ul className="enrichment-list enrichment-list--mono">
                {entity.readPatterns.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}

      <EntityInsightTiles index={index} entityId={entity.id} />

      {/* Pane 1 — schema */}
      <section style={{ marginBottom: 'var(--sp-xl)' }}>
        <div className="section-label">Schema · {entity.fields.length} fields</div>
        <table className="field-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {entity.fields.map((field) => {
              const active = selectedField === field.name;
              return (
                <tr key={field.name}>
                  <td className="field-name">
                    <button
                      type="button"
                      className="field-name-select"
                      aria-pressed={active}
                      onClick={() => navigateToEntity(entity.id, active ? undefined : field.name)}
                    >
                      {field.name}
                      {field.optional && <span style={{ color: 'var(--text3)' }}>?</span>}
                    </button>
                  </td>
                  <td>
                    {field.referencesEntity ? (
                      <button
                        className="field-type field-ref"
                        onClick={() => navigate({ kind: 'entity', id: field.referencesEntity! })}
                      >
                        {field.type}
                      </button>
                    ) : (
                      <span className="field-type">{field.type}</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text3)' }}>
                    {field.referencesEnum ? `enum ${field.referencesEnum}` : (field.description ?? '')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {selectedFieldIsReal && (
          <SelectedFieldDetail
            manifest={manifest}
            entityId={entity.id}
            fieldName={selectedField!}
            onNavigateEntity={navigateToEntity}
          />
        )}
      </section>

      {/* Pane 2 — relationships */}
      <section style={{ marginBottom: 'var(--sp-xl)' }}>
        <div className="section-label">
          Relationships · {out.length} out · {incoming.length} in
        </div>
        {out.length === 0 && incoming.length === 0 ? (
          <EmptyState
            icon="hub"
            title="No references"
            detail={getRelationshipsEmptyStateMessage(entityKind)}
          />
        ) : (
          <>
            {out.map((ref) => (
              <button
                key={`out-${ref.fromField}-${ref.to}`}
                className="ref-row"
                onClick={() => navigate({ kind: 'entity', id: ref.to })}
              >
                <Icon name="arrow_forward" size="sm" />
                <span className="mono">{ref.fromField}</span>
                <span className="ref-kind">{ref.kind}</span>
                {ref.cardinality && (
                  <span className="ref-cardinality">{ref.cardinality}</span>
                )}
                {ref.writePattern && (
                  <span className="ref-write-pattern">{ref.writePattern}</span>
                )}
                <Chip label={ref.source ?? 'prose'} tone={SOURCE_TONE[ref.source ?? 'prose']} />
                <span className="mono" style={{ marginLeft: 'auto' }}>
                  {ref.to}
                </span>
              </button>
            ))}
            {incoming.map((ref) => (
              <button
                key={`in-${ref.from}-${ref.fromField}`}
                className="ref-row"
                onClick={() => navigate({ kind: 'entity', id: ref.from })}
              >
                <Icon name="arrow_back" size="sm" />
                <span className="mono">{ref.from}</span>
                <span className="ref-kind">{ref.kind}</span>
                {ref.cardinality && (
                  <span className="ref-cardinality">{ref.cardinality}</span>
                )}
                {ref.consistency && (
                  <span className="ref-consistency">{ref.consistency}</span>
                )}
                <Chip label={ref.source ?? 'prose'} tone={SOURCE_TONE[ref.source ?? 'prose']} />
                <span className="mono" style={{ marginLeft: 'auto' }}>
                  .{ref.fromField}
                </span>
              </button>
            ))}
          </>
        )}
      </section>

      {/* Pane 3 — usage */}
      <section>
        <div className="section-label">Usage</div>
        <div className="card">
          <p>
            <strong>Persistence:</strong>{' '}
            {path ? (
              <button
                type="button"
                className="link-button"
                onClick={() => navigate({ kind: 'firestore-path', entityId: entity.id })}
              >
                {path.path} · {path.retention || 'retention unspecified'}
              </button>
            ) : (
              'ephemeral — not written to Firestore'
            )}
          </p>
          <p style={{ marginTop: 'var(--sp-sm)' }}>
            <strong>Surfaces in this domain:</strong>{' '}
            {surfaces.length > 0
              ? surfaces.map((s, i) => (
                  <span key={s.id}>
                    {i > 0 && ', '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => navigate({ kind: 'surface', id: s.id })}
                    >
                      {s.id}
                    </button>
                  </span>
                ))
              : 'none catalogued yet'}
          </p>
        </div>
      </section>

      {/* Functions that read or write this entity */}
      <section>
        <div className="section-label">Functions</div>
        <div className="card">
          {cloudFunctionsForEntity(manifest, entity.id).length > 0 ? (
            <p>
              {cloudFunctionsForEntity(manifest, entity.id).map((f, i) => (
                <span key={f.name}>
                  {i > 0 && ', '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate({ kind: 'cloud-function', id: f.name })}
                  >
                    {f.name}
                  </button>
                </span>
              ))}
            </p>
          ) : (
            <p>No cloud functions reference this entity.</p>
          )}
        </div>
      </section>

      {/* Events that carry this entity */}
      <section>
        <div className="section-label">Events</div>
        <div className="card">
          {eventsForEntity(manifest, entity.id).length > 0 ? (
            <p>
              {eventsForEntity(manifest, entity.id).map((ev, i) => (
                <span key={ev.type}>
                  {i > 0 && ', '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate({ kind: 'event-type', id: ev.type })}
                  >
                    {ev.type}
                  </button>
                </span>
              ))}
            </p>
          ) : (
            <p>No events carry this entity.</p>
          )}
        </div>
      </section>

      <RelationshipsPanel manifest={manifest} kind="entity" id={entity.id} onNavigate={navigateToObjectKind} />

      {/* Version history */}
      <EntityHistoryContainer entityId={entity.id} />

      {/* Phase 4 — collaboration */}
      <AnnotationsContainer entityId={entity.id} />
    </article>
  );
}

/**
 * Best-effort clipboard write that surfaces denial in dev tools without
 * tripping an unhandled-rejection. Clipboard rejection most often means
 * non-HTTPS context or a user permission denial — both recoverable by the
 * user, neither worth a toast.
 */
function copyToClipboard(text: string): void {
  const cb = navigator.clipboard;
  if (!cb) {
    console.debug('[atlas] clipboard unavailable; skipping copy');
    return;
  }
  cb.writeText(text).catch((err: unknown) => {
    console.debug('[atlas] clipboard denied:', err);
  });
}
